import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { toast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { SelectWithSearch } from '../ui/select-with-search';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import SelecaoMensagensTransferencia, { MensagemSelecao } from './SelecaoMensagensTransferencia';

// Parâmetros e instâncias possíveis da transferência (GET /tickets/:id/transferencia/opcoes)
interface InstanciaTransferencia { id: string; nome: string | null; tipoApi: string }
interface OpcoesTransferencia {
  historicoCompleto: boolean;
  modoInstancia: 'MANTER' | 'PERGUNTAR' | 'TRANSFERIR';
  instancia: { atual: InstanciaTransferencia | null; candidatas: InstanciaTransferencia[]; avisos: string[] };
}

export interface ResultadoTransferencia {
  ticketId: string;
  novoUsuarioId: string | null;
  novaFilaId: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticketId: string | null | undefined;
  currentUserId?: string;
  onTransferred?: (resultado: ResultadoTransferencia) => void | Promise<void>;
}

/**
 * Modal de transferência de ticket (para atendente ou fila). Conforme as configurações da empresa:
 * - sem histórico completo: quem transfere escolhe as mensagens que o próximo atendente verá;
 * - instância: informa a troca automática ou pergunta se a conversa vai para a instância do atendente.
 */
const TransferirTicketDialog: React.FC<Props> = ({ open, onOpenChange, ticketId, currentUserId, onTransferred }) => {
  const { t } = useTranslation();

  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [filas, setFilas] = useState<any[]>([]);
  const [loadingListas, setLoadingListas] = useState(false);
  const [paraUsuario, setParaUsuario] = useState('');
  const [paraFila, setParaFila] = useState('');
  const [nota, setNota] = useState('');

  const [opcoes, setOpcoes] = useState<OpcoesTransferencia | null>(null);
  const [loadingOpcoes, setLoadingOpcoes] = useState(false);
  const [mensagens, setMensagens] = useState<MensagemSelecao[]>([]);
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());
  const [loadingMensagens, setLoadingMensagens] = useState(false);
  // Modo "perguntar": '' = sem resposta, 'manter' ou o id da instância de destino
  const [escolhaInstancia, setEscolhaInstancia] = useState('');
  const [enviando, setEnviando] = useState(false);

  const carregarOpcoes = useCallback(async (novoUsuarioId?: string) => {
    if (!ticketId) return null;
    setLoadingOpcoes(true);
    try {
      const { data } = await api.get(`/tickets/${ticketId}/transferencia/opcoes`, {
        params: novoUsuarioId ? { novoUsuarioId } : {},
      });
      setOpcoes(data);
      return data as OpcoesTransferencia;
    } catch (error) {
      console.error('Erro ao carregar opções de transferência:', error);
      setOpcoes(null);
      return null;
    } finally {
      setLoadingOpcoes(false);
    }
  }, [ticketId]);

  const carregarMensagens = useCallback(async () => {
    if (!ticketId) return;
    setLoadingMensagens(true);
    try {
      const { data } = await api.get(`/tickets/${ticketId}/transferencia/mensagens`);
      setMensagens(Array.isArray(data.mensagens) ? data.mensagens : []);
    } catch (error) {
      console.error('Erro ao carregar mensagens para transferência:', error);
      setMensagens([]);
    } finally {
      setLoadingMensagens(false);
    }
  }, [ticketId]);

  // Ao abrir: limpa o formulário e carrega usuários, filas e parâmetros
  useEffect(() => {
    if (!open || !ticketId) return;
    setParaUsuario('');
    setParaFila('');
    setNota('');
    setOpcoes(null);
    setMensagens([]);
    setSelecionadas(new Set());
    setEscolhaInstancia('');

    let ativo = true;
    setLoadingListas(true);
    Promise.all([api.get('/usuarios/transferencia'), api.get('/filas/transferencia')])
      .then(([u, f]) => {
        if (!ativo) return;
        setUsuarios(Array.isArray(u.data.usuarios) ? u.data.usuarios : []);
        setFilas(Array.isArray(f.data.filas) ? f.data.filas : []);
      })
      .catch((error) => {
        console.error('Erro ao carregar usuários e filas:', error);
        if (ativo) { setUsuarios([]); setFilas([]); }
      })
      .finally(() => { if (ativo) setLoadingListas(false); });

    carregarOpcoes().then((op) => {
      if (ativo && op && !op.historicoCompleto) carregarMensagens();
    });
    return () => { ativo = false; };
  }, [open, ticketId, carregarOpcoes, carregarMensagens]);

  const escolherUsuario = (id: string) => {
    setParaUsuario(id);
    setEscolhaInstancia('');
    if (id) setParaFila('');
    // Busca as instâncias do atendente escolhido (troca de instância)
    carregarOpcoes(id || undefined);
  };

  const candidatas = paraUsuario && opcoes?.modoInstancia !== 'MANTER' ? (opcoes?.instancia?.candidatas || []) : [];
  const precisaEscolherInstancia = opcoes?.modoInstancia === 'PERGUNTAR' && candidatas.length > 0 && !escolhaInstancia;
  const avisos = paraUsuario && opcoes && opcoes.modoInstancia !== 'MANTER' ? (opcoes.instancia?.avisos || []) : [];

  const transferir = async () => {
    if (!ticketId) return;
    if (!paraUsuario && !paraFila) {
      toast({ title: t('common.error'), description: t('atendimento.toastTransferNoTarget'), variant: 'destructive' });
      return;
    }
    if (precisaEscolherInstancia) {
      toast({ title: t('common.error'), description: t('atendimento.transferChooseInstance'), variant: 'destructive' });
      return;
    }

    setEnviando(true);
    try {
      const notaInterna = nota.trim() || undefined;
      // Sem histórico completo: só as mensagens marcadas ficam visíveis para quem receber
      const mensagensVisiveis = opcoes && !opcoes.historicoCompleto ? Array.from(selecionadas) : undefined;
      let resumo: any = null;
      if (paraUsuario) {
        const { data } = await api.post('/tickets/transfer', {
          ticketId,
          novoUsuarioId: paraUsuario,
          novaFilaId: null,
          notaInterna,
          mensagensVisiveis,
          instanciaDestinoId: escolhaInstancia && escolhaInstancia !== 'manter' ? escolhaInstancia : undefined,
        });
        resumo = data?.ticket?.transferencia || null;
      } else {
        await api.patch(`/chats/${ticketId}/fila`, { filaId: paraFila, notaInterna, mensagensVisiveis });
      }

      const extras: string[] = [];
      if (resumo?.instanciaNova?.nome) extras.push(t('atendimento.transferInstanceChanged', { name: resumo.instanciaNova.nome }));
      if (Array.isArray(resumo?.avisos)) extras.push(...resumo.avisos);
      toast({ title: t('common.success'), description: [t('atendimento.toastTicketTransfered'), ...extras].join(' ') });

      onOpenChange(false);
      await onTransferred?.({ ticketId, novoUsuarioId: paraUsuario || null, novaFilaId: paraFila || null });
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error?.response?.data?.message || t('atendimento.toastTicketTransferError'),
        variant: 'destructive',
      });
    } finally {
      setEnviando(false);
    }
  };

  const historicoRestrito = !!opcoes && !opcoes.historicoCompleto;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${historicoRestrito ? 'sm:max-w-[640px]' : 'sm:max-w-[500px]'} max-h-[90vh] overflow-y-auto dark:bg-slate-800 dark:text-white`}>
        <DialogHeader>
          <DialogTitle>{t('atendimento.transferTicket')}</DialogTitle>
          <DialogDescription>{t('atendimento.transferDesc')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>{t('atendimento.transferToUser')}</Label>
            <SelectWithSearch
              options={usuarios
                .filter((u: any) => u.id !== currentUserId)
                .map((u: any) => ({ value: u.id, label: `${u.nome} (${u.email})` }))}
              value={paraUsuario ? [{ value: paraUsuario, label: '' }] : []}
              onChange={(val) => escolherUsuario(val[0]?.value || '')}
              placeholder={t('atendimento.selectUserPlaceholder')}
              loading={loadingListas}
              disabled={loadingListas}
              single
            />
          </div>
          <div className="flex items-center justify-center text-sm text-gray-500">{t('atendimento.or')}</div>
          <div className="grid gap-2">
            <Label>{t('atendimento.transferToQueue')}</Label>
            <SelectWithSearch
              options={filas.map((f: any) => ({ value: f.id, label: f.nome }))}
              value={paraFila ? [{ value: paraFila, label: '' }] : []}
              onChange={(val) => {
                const id = val[0]?.value || '';
                setParaFila(id);
                if (id) { setParaUsuario(''); setEscolhaInstancia(''); }
              }}
              placeholder={t('atendimento.selectQueuePlaceholder')}
              loading={loadingListas}
              disabled={loadingListas}
              single
            />
          </div>

          {/* Instância: o atendente escolhido tem instância vinculada */}
          {paraUsuario && loadingOpcoes && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
          )}
          {paraUsuario && !loadingOpcoes && opcoes?.modoInstancia === 'PERGUNTAR' && candidatas.length > 0 && (
            <div className="grid gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-950/40">
              <Label>{t('atendimento.transferInstanceQuestion')}</Label>
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" name="transferInstancia" checked={escolhaInstancia === 'manter'}
                  onChange={() => setEscolhaInstancia('manter')} />
                {t('atendimento.transferKeepInstance', { name: opcoes.instancia.atual?.nome || '-' })}
              </label>
              {candidatas.map((inst) => (
                <label key={inst.id} className="flex items-center gap-2 text-sm">
                  <input type="radio" name="transferInstancia" checked={escolhaInstancia === inst.id}
                    onChange={() => setEscolhaInstancia(inst.id)} />
                  {t('atendimento.transferMoveInstance', { name: inst.nome || '-' })}
                </label>
              ))}
            </div>
          )}
          {paraUsuario && !loadingOpcoes && opcoes?.modoInstancia === 'TRANSFERIR' && candidatas.length > 0 && (
            <p className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm dark:border-blue-900 dark:bg-blue-950/40">
              {t('atendimento.transferInstanceAuto', { name: candidatas[0].nome || '-' })}
            </p>
          )}
          {!loadingOpcoes && avisos.length > 0 && (
            <ul className="list-disc pl-5 text-xs text-amber-700 dark:text-amber-400">
              {avisos.map((aviso, i) => <li key={i}>{aviso}</li>)}
            </ul>
          )}

          {/* Histórico restrito: quem transfere escolhe o que o outro atendente verá */}
          {historicoRestrito && (
            <div className="grid gap-2">
              <Label>{t('atendimento.transferHistoryLabel')}</Label>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('atendimento.transferHistoryHint')}</p>
              <SelecaoMensagensTransferencia
                mensagens={mensagens}
                selecionadas={selecionadas}
                onChange={setSelecionadas}
                loading={loadingMensagens}
              />
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="transferNota">{t('atendimento.internalNoteLabel')}</Label>
            <Textarea
              id="transferNota"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder={t('atendimento.internalNotePlaceholder')}
              maxLength={1000}
              rows={3}
              className="dark:bg-slate-700 dark:text-white"
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={transferir}
            disabled={(!paraUsuario && !paraFila) || enviando || loadingOpcoes || loadingMensagens || precisaEscolherInstancia}
          >
            {t('atendimento.transfer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TransferirTicketDialog;
